export function Privacy() {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: February 23, 2026</p>

        <div className="legal-content">
          <h2>1. Introduction</h2>
          <p>
            HAJ Solutions, Inc. (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates
            CryptoGuardians. This Privacy Policy explains how we collect, use, disclose, and
            safeguard your information when you use CryptoGuardians.
          </p>

          <h2>2. Information We Collect</h2>

          <h3>Wallet Address</h3>
          <p>
            Your Ethereum wallet address is used as your account identifier. This is collected
            during Sign-In with Ethereum (SIWE) authentication and is required to provide the
            service.
          </p>

          <h3>Email Address</h3>
          <p>
            If you provide an email address (e.g., through our support form or
            account communication), we collect and store it for communication and service-related
            purposes.
          </p>

          <h3>Usage Analytics</h3>
          <p>
            We collect anonymized usage data such as feature usage frequency, service access
            patterns, and session information to improve our product.
          </p>

          <h3>Support Messages</h3>
          <p>
            Messages submitted through our support form, including email address, category, and
            message content, are stored to process and respond to your request.
          </p>

          <h3>Payment Information</h3>
          <p>
            Subscription payments are processed by{' '}
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
              Stripe, Inc.
            </a>{' '}
            We do not directly store credit card numbers, banking details, or other sensitive
            financial information. Stripe&apos;s handling of your payment data is governed by{' '}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe&apos;s Privacy Policy
            </a>
            .
          </p>

          <h3>IP Addresses</h3>
          <p>
            IP addresses are hashed using HMAC-SHA256 before storage. We use hashed IPs for rate
            limiting and abuse prevention only. We do not store raw IP addresses.
          </p>

          <h3>Terms Acceptance Records</h3>
          <p>
            We record the date and time you accept our Terms of Service, Privacy Policy, and Risk
            Disclaimer. This record is associated with your wallet address and is retained for
            legal compliance purposes.
          </p>

          <h2>3. Information We Do NOT Collect</h2>
          <p>
            <strong>CryptoGuardians does not collect, store, access, or transmit:</strong>
          </p>
          <ul>
            <li>
              <strong>Private keys</strong> &mdash; Your private keys never leave your wallet.
              CryptoGuardians has no access to your private keys at any time.
            </li>
            <li>
              <strong>Seed phrases (recovery phrases)</strong> &mdash; We never request, collect,
              or store wallet seed phrases or mnemonic recovery phrases.
            </li>
            <li>
              <strong>Wallet balances or holdings</strong> &mdash; We do not query or store the
              balance of your wallet. Our analysis is limited to transaction-level risk assessment.
            </li>
          </ul>
          <p>
            <strong>
              If anyone claiming to represent CryptoGuardians asks for your private key or seed
              phrase, it is a scam. We will never ask for this information.
            </strong>
          </p>

          <h2>4. How We Use Your Information</h2>
          <ul>
            <li>To provide, operate, and maintain CryptoGuardians</li>
            <li>To process subscriptions and payments via Stripe</li>
            <li>To respond to support requests</li>
            <li>To detect and prevent fraud, abuse, and security threats</li>
            <li>To improve our product and user experience</li>
            <li>To communicate service updates and important notices</li>
            <li>To comply with legal obligations and enforce our Terms of Service</li>
          </ul>

          <h2>5. No Selling of Personal Data</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We will
            never monetize your data.
          </p>

          <h2>6. Information Sharing</h2>
          <p>We may share information only in these limited circumstances:</p>
          <ul>
            <li>
              <strong>Stripe:</strong> Payment and subscription data is shared with Stripe for
              payment processing.
            </li>
            <li>
              <strong>Infrastructure Providers:</strong> We use cloud hosting services to operate
              CryptoGuardians, subject to confidentiality agreements.
            </li>
            <li>
              <strong>Legal Requirements:</strong> We may disclose information if required by law,
              regulation, or legal process.
            </li>
          </ul>

          <h2>7. Data Security</h2>
          <p>We implement security measures to protect your information, including:</p>
          <ul>
            <li>Encryption in transit (TLS/SSL) and at rest</li>
            <li>HMAC-SHA256 hashing of IP addresses</li>
            <li>JWT-based session management with expiration</li>
            <li>Access controls and principle of least privilege</li>
            <li>Regular security assessments</li>
            <li>Non-custodial architecture &mdash; we never hold or access your digital assets</li>
          </ul>

          <h2>8. Data Retention</h2>
          <p>
            We retain your data only as long as necessary to provide the service. Wallet addresses
            and subscription data are retained for the duration of your active subscription plus a
            reasonable period for legal and accounting purposes. Support messages are retained for
            up to 24 months. You may request deletion of your data at any time.
          </p>

          <h2>9. Your Rights</h2>
          <p>
            Depending on your jurisdiction, you may have rights regarding your personal data,
            including access, correction, deletion, and portability. To exercise these rights,
            contact us at{' '}
            <a href="mailto:privacy@cryptoguardians.io">privacy@cryptoguardians.io</a>.
          </p>

          <h2>10. Cookies</h2>
          <p>
            CryptoGuardians uses essential cookies and localStorage for session management (JWT
            authentication). We do not use third-party tracking cookies or advertising cookies.
          </p>

          <h2>11. Children</h2>
          <p>
            CryptoGuardians is not directed to individuals under 18. We do not knowingly collect
            personal information from minors.
          </p>

          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted on this
            page with a revised effective date. Continued use of CryptoGuardians after changes
            constitutes acceptance of the updated policy.
          </p>

          <h2>13. Contact</h2>
          <p>Questions about this Privacy Policy? Contact us:</p>
          <ul>
            <li>
              Privacy: <a href="mailto:privacy@cryptoguardians.io">privacy@cryptoguardians.io</a>
            </li>
            <li>
              Billing: <a href="mailto:billing@cryptoguardians.io">billing@cryptoguardians.io</a>
            </li>
            <li>
              Support: <a href="mailto:support@cryptoguardians.io">support@cryptoguardians.io</a>
            </li>
          </ul>

          <p className="legal-updated" style={{ marginTop: '3rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
            CryptoGuardians is a product of HAJ Solutions Inc.
          </p>
        </div>
      </div>
    </div>
  );
}
